/**
 * PricingRuleFieldSwitch — an inline, optimistic on/off Switch that persists a
 * single boolean field on this Pricing Rule (e.g. `disable`, `storefront_enabled`).
 *
 * Mirrors the Item list's StorefrontToggleCell pattern: optimistic local state,
 * a mutation via the shared `pricingRules.update` helper, a toast on success/failure,
 * and — because the field lives on the doc StandardDetailPage loaded — it invalidates
 * the doc query (["get","Pricing Rule",docName]) so the status pill, countdown and
 * summary all re-read the new value. No new endpoint.
 *
 * `invert` handles ERPNext's `disable` field, where the switch reads "on" (live)
 * but the stored value is the opposite (disable = on ? 0 : 1).
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { pricingRules } from "@/lib/erp/pricingRules";

export function PricingRuleFieldSwitch({
  docName,
  field,
  checked,
  invert = false,
  ariaLabel,
  msgOn,
  msgOff,
  errMsg,
  className,
}: {
  docName: string;
  field: string;
  /** What the switch shows (already de-inverted — true = live/on). */
  checked: boolean;
  /** When true, the stored value is the opposite of the switch (for `disable`). */
  invert?: boolean;
  ariaLabel: string;
  msgOn?: string;
  msgOff?: string;
  errMsg?: string;
  className?: string;
}) {
  const [on, setOn] = useState(checked);
  const queryClient = useQueryClient();

  // Re-sync when the doc refetches (e.g. after invalidation or a full save).
  useEffect(() => setOn(checked), [checked]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (next: boolean) => {
      const stored = invert ? (next ? 0 : 1) : next ? 1 : 0;
      return pricingRules.update(docName, { [field]: stored });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get", "Pricing Rule", docName] });
    },
  });

  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const prev = on;
    const next = !on;
    setOn(next);
    try {
      await mutateAsync(next);
      const msg = next ? msgOn : msgOff;
      if (msg) toast.success(msg);
    } catch {
      setOn(prev);
      toast.error(errMsg ?? "บันทึกไม่สำเร็จ ลองอีกครั้ง");
    }
  };

  return (
    <Switch
      checked={on}
      onCheckedChange={() => {}}
      onClick={handleToggle}
      disabled={isPending || !docName}
      aria-label={ariaLabel}
      className={className}
    />
  );
}
